import { createMachine, assign } from 'xstate';
import { reportError } from 'venuescanner-js/react/services/errorService';

import {
  markEnquiryAsBooked,
  nudgeHost,
  reopenEnquiry,
  submitQuote,
  submitUpdatedEventDetails,
  unmarkEnquiryAsBooked,
  cancelThread,
  bookThread,
  updateBookingPrice,
} from './api';
import { isHost, isOrganiser } from 'venuescanner-js/constants/user_type';
import { actions } from './actions';
import { guards } from './guards';

export const enquiryMachine = createMachine(
  {
    id: 'enquiryMachine',
    initial: 'init',
    context: {
      thread: null,
      space: null,
      venue: null,
      organiser: null,
      concierge: null,
      host: null,
      cancellationReason: null,
      error: null,
    },
    on: {
      MARK_AS_BOOKED: [
        {
          target: 'confirmBookingPrice',
          cond: (context, _event) => isHost(context.thread),
        },
        {
          target: 'markingAsBooked',
          cond: (context, _event) => isOrganiser(context.thread),
        },
      ],
      UNMARK_AS_BOOKED: 'unmarkingAsBooked',
      CANCEL: 'confirmCancellation',
      EDIT_EVENT: 'editEvent',
    },
    states: {
      init: {
        entry: ['setActiveViewToChat'],
        always: [
          { target: 'booked', cond: 'isStatusBooked' },
          { target: 'expired', cond: 'isStatusExpired' },
          { target: 'cancelled', cond: 'isStatusCancelled' },
          { target: 'quoteRequested', cond: 'isStatusQuoteRequested' },
          { target: 'quoteReceived', cond: 'isStatusQuoteReceived' },
          { target: 'open', cond: 'isStatusNewOrOpen' },
        ],
      },
      open: {
        on: {
          EDIT_QUOTE: 'editQuote',
          REQUEST_QUOTE: 'requestQuote',
          THREAD_CHANGED: {
            actions: ['refreshThread', 'refreshCount'],
            target: 'init',
          },
        },
      },
      editEvent: {
        on: {
          SEND_EVENT_DETAILS: 'submittingEventDetails',
          BACK: 'init',
          THREAD_CHANGED: {
            actions: ['refreshThread', 'refreshCount'],
            target: 'init',
          },
        },
      },
      editQuote: {
        on: {
          SEND_QUOTE: 'submittingQuote',
          BACK: 'init',
          THREAD_CHANGED: {
            actions: ['refreshThread', 'refreshCount'],
            target: 'init',
          },
          MARK_AS_BOOKED: 'markingAsBooked',
          CLOSE: 'confirmCancellation',
        },
      },
      submittingEventDetails: {
        invoke: {
          id: 'submitEnquiry',
          src: (context, event) =>
            submitUpdatedEventDetails(context, event.details),
          onDone: {
            target: 'enquiryEditSuccess',
            actions: ['updateThread', 'refreshCount'],
          },
          onError: {
            target: 'failure',
            actions: assign({
              error: (context, event) => event,
            }),
          },
        },
      },
      enquiryEditSuccess: {
        after: {
          SUCCESS_DELAY: {
            actions: ['setActiveViewToChat'],
            target: 'init',
          },
        },
      },
      submittingQuote: {
        invoke: {
          id: 'submitQuote',
          src: (context, event) => submitQuote(context, event.quote),
          onDone: {
            target: 'quoteEditSuccess',
            actions: ['updateThread', 'refreshCount'],
          },
          onError: {
            target: 'failure',
            actions: assign({ error: (context, event) => event }),
          },
        },
      },
      quoteEditSuccess: {
        after: {
          SUCCESS_DELAY: [
            {
              target: 'subscriptionPromo',
              cond: ({ venue }, _event) => !venue.sub_plan,
            },
            {
              target: 'stripePromo',
              cond: ({ venue }, _event) => !venue.stripe_connected_account_id,
            },
            {
              actions: ['setActiveViewToChat'],
              target: 'quoteReceived',
            },
          ],
        },
      },
      stripePromo: {
        on: {
          SKIP: {
            actions: ['setActiveViewToChat'],
            target: 'quoteReceived',
          },
        },
      },
      subscriptionPromo: {
        on: {
          SKIP: {
            actions: ['setActiveViewToChat'],
            target: 'quoteReceived',
          },
        },
      },
      reopen: {
        on: {
          REOPENING_CONFIRMED: 'reopeningEnquiry',
          BACK: 'cancelled',
          MARK_AS_BOOKED: 'markingAsBooked',
          CLOSE: 'confirmCancellation',
          THREAD_CHANGED: {
            actions: ['refreshThread', 'refreshCount'],
            target: 'init',
          },
        },
      },
      reopeningEnquiry: {
        invoke: {
          id: 'reopenEnquiry',
          src: (_context, event) => reopenEnquiry(event.enquiry),
          onDone: {
            target: 'open',
            actions: ['setActiveViewToChat', 'updateThread', 'refreshCount'],
          },
          onError: {
            target: 'failure',
            actions: assign({ error: (context, event) => event }),
          },
        },
      },
      confirmCancellation: {
        on: {
          CANCELLATION_CONFIRMED: {
            target: 'cancelled',
          },
          SET_SETTINGS: [
            {
              actions: ['saveCancellationReason'],
              target: 'setSettings',
              cond: (context, _event) => isHost(context.thread),
            },
            {
              target: 'cancelled',
            },
          ],
          THREAD_CHANGED: {
            actions: ['refreshThread', 'refreshCount'],
            target: 'init',
          },
          BACK: 'init',
        },
      },
      setSettings: {
        on: {
          CANCELLATION_CONFIRMED: 'cancelled',
        },
      },
      cancelled: {
        on: {
          REOPEN_ENQUIRY: 'reopen',
          THREAD_CHANGED: {
            actions: ['refreshThread', 'refreshCount'],
            target: 'init',
          },
        },
      },
      booked: {
        on: {
          CANCEL: 'confirmCancellation',
          THREAD_CHANGED: {
            actions: ['refreshThread', 'refreshCount'],
            target: 'init',
          },
          NOT_A_BOOKING: {
            target: 'confirmCancellation',
          },
          CONFIRM_BOOKING: [
            {
              target: 'confirmBookingPrice',
              cond: (context, _event) => isHost(context.thread),
            },
            {
              target: 'markingAsBooked',
              cond: (context, _event) => isOrganiser(context.thread),
            },
          ],
        },
      },
      expired: {
        on: {
          NOT_A_BOOKING: {
            target: 'cancellingThread',
          },
          THREAD_CHANGED: {
            actions: ['refreshThread', 'refreshCount'],
            target: 'init',
          },
        },
      },
      requestQuote: {
        on: {
          BACK: {
            actions: ['setActiveViewToChat'],
            target: 'open',
          },
          SUBMIT_QUOTE_REQUEST: 'submittingQuoteRequest',
          THREAD_CHANGED: {
            actions: ['refreshThread', 'refreshCount'],
            target: 'init',
          },
        },
      },
      submittingQuoteRequest: {
        invoke: {
          id: 'nudgeHost',
          src: (_context, event) => nudgeHost(event.quoteRequest),
          onDone: {
            target: 'quoteRequestedSuccess',
            actions: ['updateThread', 'refreshCount'],
          },
          onError: {
            target: 'failure',
            actions: assign({ error: (_context, event) => event }),
          },
        },
      },
      quoteRequestedSuccess: {
        after: {
          SUCCESS_DELAY: {
            actions: ['setActiveViewToChat'],
            target: 'quoteRequested',
          },
        },
      },
      quoteRequested: {
        on: {
          EDIT_QUOTE: 'editQuote',
          CANCEL: 'confirmCancellation',
          THREAD_CHANGED: {
            actions: ['refreshThread', 'refreshCount'],
            target: 'init',
          },
        },
      },
      quoteReceived: {
        on: {
          EDIT_QUOTE: 'editQuote',
          CANCEL: 'confirmCancellation',
          THREAD_CHANGED: {
            actions: ['refreshThread', 'refreshCount'],
            target: 'init',
          },
          BOOK: [
            {
              target: 'summary',
              cond: (context, _event) => {
                return isOnlinePayment(context.thread);
              },
            },
            {
              target: 'booking',
            },
          ],
        },
      },
      summary: {},
      booking: {
        invoke: {
          id: 'bookingEvent',
          src: (context, _event) => bookThread(context.thread._id),
          onDone: {
            target: ['booked'],
            actions: ['updateThread', 'refreshCount'],
          },
          onError: {
            target: 'failure',
            actions: assign({
              error: (context, event) => event,
            }),
          },
        },
      },
      markingAsBooked: {
        invoke: {
          id: 'markingEnquiryAsBooked',
          src: (context, _event) => {
            return markEnquiryAsBooked(context.thread._id);
          },
          onDone: [
            {
              cond: (context, _event) => isHost(context.thread),
              target: 'markingAsBookedSuccess',
              actions: ['updateThread', 'refreshCount'],
            },
            {
              cond: (context, _event) => isOrganiser(context.thread),
              target: 'booked',
              actions: ['setActiveViewToChat', 'updateThread', 'refreshCount'],
            },
          ],
          onError: {
            target: 'failure',
            actions: assign({ error: (context, event) => event }),
          },
        },
      },
      markingAsBookedSuccess: {
        after: {
          SUCCESS_DELAY: {
            actions: ['setActiveViewToChat'],
            target: 'booked',
          },
        },
      },
      unmarkingAsBooked: {
        invoke: {
          id: 'unmarkingEnquiryAsBooked',
          src: (context, _event) => unmarkEnquiryAsBooked(context.thread._id),
          onDone: {
            target: 'init',
            actions: ['setActiveViewToChat', 'updateThread', 'refreshCount'],
          },
          onError: {
            target: 'failure',
            actions: assign({ error: (context, event) => event }),
          },
        },
      },
      cancellingThread: {
        invoke: {
          id: 'cancellingThread',
          src: (context, _event) => cancelThread(context.thread),
          onDone: {
            target: 'cancelled',
            actions: ['setActiveViewToChat', 'updateThread', 'refreshCount'],
          },
          onError: {
            target: 'failure',
            actions: assign({
              error: (context, event) => event,
            }),
          },
        },
      },
      confirmBookingPrice: {
        on: {
          THREAD_CHANGED: {
            actions: ['refreshThread', 'refreshCount'],
            target: 'init',
          },
          CONFIRM_PRICE: 'confirmingPrice',
          SKIP_PRICE_CONFIRMATION: 'markingAsBooked',
          BACK: 'init',
          CLOSE: 'confirmCancellation',
        },
      },
      confirmingPrice: {
        invoke: {
          id: 'confirmingPrice',
          src: (context, event) =>
            updateBookingPrice(context.thread, event.price),
          onDone: {
            target: 'markingAsBooked',
          },
          onError: {
            target: 'failure',
            actions: assign({
              error: (context, event) => event,
            }),
          },
        },
      },
      failure: {
        entry: context => {
          reportError(
            'Inbox Enquiry Panel',
            context.error.data,
            {},
            {
              'thread.id': context.thread?._id,
              'space.id': context.thread?._space._id,
              'venue.id': context.thread?._venue._id,
              'organiser.id': context.thread?._organiser._id,
              'host.id': context.thread?._host._id,
              'user.type': context.thread?.viewerRole,
              type: context.error.type,
            },
          );
        },
        // Always go to init rather than staying in this state
        // Since we don't handle errors apart from saying Oops
        // I think it's a slightly better UX to go back to
        // the main enquiry state and maybe retry over seeing no cta.
        always: {
          target: 'init',
        },
      },
    },
  },
  {
    guards,
    delays: {
      SUCCESS_DELAY: ({ successDelay }, _event) => successDelay,
    },
    actions,
  },
);

function isOnlinePayment(thread) {
  const { payment_required_to_book: paymentRequiredToBook, pendingPayment } =
    thread;

  return (
    isOrganiser(thread) &&
    paymentRequiredToBook &&
    pendingPayment &&
    pendingPayment.amount
  );
}
